import * as React from "react";
import { createFilterOptions, FormControl, TextField } from "@mui/material";
import { Field, useFormikContext } from "formik";
import { Autocomplete, AutocompleteRenderInputParams } from "formik-mui";
import { useTranslation } from "react-i18next";
import { SelectOption } from "@mui/base";

interface Props {
  name: string;
  label?: string;
  placeholder?: string;
  options: SelectOption<string>[];
  areOptionsLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  helperText?: string;
  onInputTextChange?: (value: string) => void;
  onSelect?: (value: SelectOption<string>) => void;
  suppressFilterOptions?: boolean;
  matchFrom?: "start" | "any";
  variant?: "outlined" | "filled" | "standard";
  size?: "small" | "medium";
}

const createDefaultFilterOptions = (matchFrom: "start" | "any" = "start") =>
  createFilterOptions({
    matchFrom,
    trim: true
  });

const doNotFilterOptions = (x: SelectOption<string>) => x;

const SelectOneAutocompleteField: React.FC<Props> = ({
  name,
  label,
  placeholder,
  options,
  areOptionsLoading,
  required,
  disabled,
  isLoading,
  isError,
  helperText,
  onInputTextChange,
  onSelect,
  suppressFilterOptions,
  matchFrom = "start",
  variant = "standard",
  size
}) => {
  const { t } = useTranslation();
  const { errors, touched, isSubmitting, setFieldValue } = useFormikContext<Record<string, string>>();

  const validate = React.useCallback(
    (value: string) => (required && !value ? t("common.validation.required") : undefined),
    [required, t]
  );

  const isOptionEqualToValue = React.useCallback(
    (option: SelectOption<string>, value: string | SelectOption<string>) =>
      option === value || option.value === value || option.value === (value as SelectOption<string>).value,
    []
  );

  const onInputChange = React.useCallback(
    (event, newInputValue: string) => {
      if (onInputTextChange) {
        onInputTextChange(newInputValue);
      }
    },
    [onInputTextChange]
  );

  const onChange = React.useCallback(
    (event, value: SelectOption<string>, reason: string) => {
      setFieldValue(name, value);
      if (onSelect && reason === "selectOption") {
        onSelect(value);
      }
    },
    [name, onSelect, setFieldValue]
  );

  const filterOptions = React.useMemo(
    () => (suppressFilterOptions ? doNotFilterOptions : createDefaultFilterOptions(matchFrom)),
    [suppressFilterOptions, matchFrom]
  );

  return (
    <FormControl fullWidth>
      <Field
        component={Autocomplete}
        type="text"
        name={name}
        label={label}
        placeholder={isLoading ? "Loading..." : (isError && "Error") || placeholder}
        options={options}
        variant={variant}
        disabled={disabled || isLoading || isError || isSubmitting}
        required={required}
        validate={validate}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={filterOptions}
        onInputChange={onInputChange}
        onChange={onChange}
        loading={areOptionsLoading}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            // We have to manually set the corresponding fields on the input component
            name={name}
            error={(touched[name] && !!(errors[name] as string)) || isError}
            helperText={(touched[name] && (errors[name] as string)) || helperText}
            label={label}
            variant={variant}
            required={required}
            disabled={isSubmitting}
            placeholder={isLoading ? "Loading..." : (isError && "Error") || placeholder}
            size={size}
          />
        )}
      />
    </FormControl>
  );
};

export { SelectOneAutocompleteField };
